import React, { useState } from 'react';
import type {
  SiteComposite,
  PillarCompositeScore,
  PillarConstraint,
  PillarFactContribution,
  PillarMissingFact,
  FactProvenance,
} from '../../../types/portfolioComposite';
import {
  PILLARS,
  PILLAR_IDS,
  inapplicablePillarLabel,
  isAdvisoryPillar,
  type PillarId,
} from '../../../lib/portfolio/pillarRegistry';
import { evaluateSiteDataAdequacy, siteVerdictWithheld } from '../../../lib/portfolio/deriveSiteVerdict';
import { displayPillarScore } from '../../../lib/portfolio/compositeScorer';
import { StatusPill, type StatusPillVariant } from '../../ui/status-pill';
import { ChevronDown, ChevronRight, Microscope, Target, FileText, CircleDashed } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { bandStatusPillVariant } from '../../shared/verdictVisuals';
import type { DecisionBandColor } from '../../../lib/portfolio/decisionBands';
import { bandColorForId } from '../../../lib/portfolio/portfolioViewHelpers';
import type { SiteDocument } from '../../../types/siteRag';
import { DocViewerDialog } from '../DocViewerDialog';
import { EvidenceQualityDetails } from './EvidenceQualityDetails';
import { VERDICT_WITHHELD_LABEL } from '../../../lib/portfolio/decisionBands';

export interface SiteDeepDiveTabProps {
  siteComposite: SiteComposite | null;
  onViewReport: (reportId: string) => void;
  siteDocuments?: SiteDocument[];
  /**
   * RP4 — funnel a low-pillar / "Insufficient evidence" deep dive into the
   * resolve loop. The host opens the Decide surface, which lists what still
   * holds GO (it used to open the retired Path to GO tab).
   * Absent → the withheld header degrades to its passive (non-CTA) form.
   */
  onResolveWithheld?: () => void;
  /**
   * Part I S4 — true when the cached composite is STALE (a report changed its
   * inputs and the read-side recompute has not landed yet). When stale the
   * numeric pillar/verdict render is the PRE-REPORT composite, so we show
   * "Recomputing scores…" instead of stale numbers / a false "No source
   * reports". The host computes this via `isSiteCompositeStale(siteComposite)`
   * and is already firing the recompute effect.
   */
  recomputing?: boolean;
}

function getRiskColorFromScore(score: number): DecisionBandColor {
  if (score <= 2.0) return 'green';
  if (score <= 3.0) return 'amber';
  if (score <= 4.0) return 'orange';
  return 'red';
}

function getRiskLabelFromScore(score: number): string {
  if (score <= 2.0) return 'Low Risk';
  if (score <= 3.0) return 'Moderate Risk';
  if (score <= 4.0) return 'High Risk';
  return 'Critical Risk';
}

/**
 * Part C SP2 — map a binding-constraint kind to a band colour so the "what moved
 * this score" chip's StatusPill tone matches the severity. A hard floor (firm-
 * capacity / no-source) reads red; a penalty (data-confidence / stale) reads
 * amber. These constraints only ever WORSEN a score, so none map to green.
 */
function constraintBandColor(kind: PillarConstraint['kind']): DecisionBandColor {
  if (kind === 'firm-capacity-floor' || kind === 'no-source-floor') return 'red';
  return 'amber';
}

/**
 * L-tier pillar-scoring rebuild — provenance chip tone per fact source.
 * Verified typed slots read confident (success/custom); developer claims read
 * cautionary (warn); the prose-heuristic fallback reads as the weakest basis
 * (conditional-high) so a prose-scored pillar is visually distinct at a glance.
 */
const PROVENANCE_PILL_VARIANT: Record<FactProvenance, StatusPillVariant> = {
  'sed-slot': 'success',
  'report-stamp': 'custom',
  'site-context-claim': 'warn',
  derived: 'neutral',
  'prose-heuristic': 'conditional-high',
};

const PROVENANCE_LABEL: Record<FactProvenance, string> = {
  'sed-slot': 'SED slot',
  'report-stamp': 'Report',
  'site-context-claim': 'Claim',
  derived: 'Derived',
  'prose-heuristic': 'Prose heuristic',
};

/** Append the unit only when the display value is a bare number ("5.2" → "5.2 km"). */
function factValueWithUnit(fc: PillarFactContribution): string {
  if (fc.unit && /^[\d.,–-]+$/.test(fc.value)) return `${fc.value} ${fc.unit}`;
  return fc.value;
}

export const SiteDeepDiveTab: React.FC<SiteDeepDiveTabProps> = ({
  siteComposite,
  onViewReport,
  siteDocuments = [],
  onResolveWithheld,
  recomputing = false,
}) => {
  const [expandedPillars, setExpandedPillars] = useState<Set<PillarId>>(new Set());
  const [viewingDoc, setViewingDoc] = useState<SiteDocument | null>(null);
  const [viewingPage, setViewingPage] = useState<number>(1);

  const togglePillar = (id: PillarId) => {
    setExpandedPillars(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!siteComposite) {
    return (
      <div className="p-8 text-center" data-testid="deepdive-empty">
        <Microscope className="w-12 h-12 text-text-muted mx-auto mb-4 opacity-50" />
        <h3 className="type-section text-foreground">No scoring data available</h3>
        <p className="type-body text-text-secondary max-w-md mx-auto mt-2">
          Generate an executive summary or feasibility report to compute the site's overall portfolio score and unlock the deep dive view.
        </p>
      </div>
    );
  }

  // Part I S4 — STALE render-guard. When the cached composite is stale (a report
  // changed its inputs since it was last computed) the numbers below are the
  // PRE-REPORT composite — showing them, or the "No source reports" floor a
  // pre-report composite carries, would assert wrong/zero scores on a site that
  // HAS reports. The host is already recomputing; surface that instead.
  if (recomputing) {
    return (
      <div className="p-8 text-center" data-testid="deepdive-recomputing">
        <Microscope className="w-12 h-12 text-primary mx-auto mb-4 opacity-70 animate-pulse" />
        <h3 className="type-section text-foreground">Recomputing scores…</h3>
        <p className="type-body text-text-secondary max-w-md mx-auto mt-2">
          A report changed the inputs to this site's score. Re-running the pillar scoring engine — this takes a moment.
        </p>
      </div>
    );
  }

  // SP0 #2 — never assert a confident verdict on absent/decoupled evidence. The deepdive reads the
  // SAME adequacy gate the executive rollup uses; when inadequate we show "Insufficient evidence"
  // instead of a hard NO-GO computed from mostly-imputed worst-case pillars.
  const adequacy = evaluateSiteDataAdequacy(siteComposite);
  const coveragePct = Math.round(adequacy.coverageRatio * 100);
  // P1.4c — a decision hold withholds the headline exactly like an evidence gap.
  const withheld = siteVerdictWithheld(siteComposite);
  const withheldLabel = VERDICT_WITHHELD_LABEL;
  const withheldReason = siteComposite.decisionHold?.reason ?? adequacy.reason;

  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="site-deepdive-tab">
      <section className="rounded-lg border border-border-subtle bg-card/40 shadow-card backdrop-blur-sm p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="type-h3 flex items-center gap-2">
              <Microscope className="w-5 h-5 text-primary" />
              Scoring Engine
            </h2>
            <p className="type-body text-text-secondary mt-1">
              See what supports each score and what needs more evidence. Open a topic to review its inputs and sources.
            </p>
          </div>
          {!withheld ? (
            <div className="flex items-center gap-3 bg-state-hover px-4 py-3 rounded border border-border">
              <div className="text-right">
                <p className="type-caption text-text-muted">Overall risk · lower is better</p>
                <p className="text-2xl font-mono font-medium text-foreground">
                  {siteComposite.riskScore.toFixed(2)}
                </p>
              </div>
              <StatusPill variant={bandStatusPillVariant(bandColorForId(siteComposite.decisionBand))}>
                {siteComposite.decisionBandLabel || getRiskLabelFromScore(siteComposite.riskScore)}
              </StatusPill>
            </div>
          ) : (
            <div
              className="flex items-center gap-3 bg-state-hover px-4 py-3 rounded border border-border-subtle"
              data-testid="deepdive-insufficient-evidence"
            >
              <div className="text-right">
                <p className="type-caption text-text-muted">Verdict</p>
                <p className="text-base font-medium text-text-secondary">{withheldLabel}</p>
                <p className="text-xs text-text-muted mt-0.5">
                  {coveragePct}% pillar coverage{withheldReason ? ` · ${withheldReason}` : ''}
                </p>
              </div>
              <StatusPill variant="neutral">Withheld</StatusPill>
              {/* RP4 — funnel the withheld deep dive into the resolve loop. */}
              {onResolveWithheld && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onResolveWithheld}
                  data-testid="deepdive-resolve-cta"
                >
                  <Target className="w-3.5 h-3.5 mr-1.5" />
                  Review decision requirements
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="type-section text-text-muted">Scored topics</h3>
          <p className="text-sm text-text-secondary">Topic scores: 0–5 · higher is better</p>
        </div>
        <div className="grid gap-3">
          {PILLAR_IDS.map((pillarId) => {
            const def = PILLARS[pillarId];
            const scoreData = siteComposite.pillars[pillarId];
            const isExpanded = expandedPillars.has(pillarId);

            // Part I S5 — route the per-pillar zero-state through the shared
            // `displayPillarScore` accessor (the single source of truth for the
            // 3 zero-states). `stale: false` here — the whole-tab STALE guard
            // above already returns "Recomputing scores…" before this loop, so
            // a stale composite never reaches the per-pillar render.
            //
            // SP0 #3 — a MISSING pillar (no data, or flagged missing → imputed worst-case) must read as
            // "Awaiting Data", NOT a confident measured-bad score. Otherwise a no-evidence site shows ten
            // red "Critical" pillars indistinguishable from a genuinely poor one. A genuine measured 0
            // (kind: 'measured', score 0) still renders its numeric score.
            const display = displayPillarScore(scoreData, { stale: false });
            if (display.kind === 'not-applicable') {
              return (
                <div
                  key={pillarId}
                  className="rounded border border-border-subtle bg-background/30 p-4"
                  data-testid={`deepdive-not-applicable-${pillarId}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-text-secondary">{def.label}</span>
                    {/* Advisory pillars keep their "Optional" tag; an inactive
                        model-dependent pillar has no "Available later" badge
                        (owner, 2026-09-30) — its reason line says what unlocks it. */}
                    {isAdvisoryPillar(pillarId) ? (
                      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                        {inapplicablePillarLabel(pillarId)}
                      </span>
                    ) : null}
                  </div>
                  {display.reason ? (
                    <p className="mt-1 max-w-2xl text-xs leading-relaxed text-text-muted">
                      {display.reason}
                    </p>
                  ) : null}
                </div>
              );
            }
            if (!scoreData || display.kind !== 'measured') {
              return (
                <div key={pillarId} className="rounded border border-border bg-state-hover/30 p-4 opacity-60">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-text-muted">{def.label}</span>
                    <span className="text-xs text-text-muted uppercase tracking-wider">Awaiting Data</span>
                  </div>
                </div>
              );
            }

            // scoreData.score is a BENEFIT score (0–5, higher = better; pillarScorer.ts). The risk-scale
            // label/colour helpers expect 1=good→5=bad, so convert benefit→risk (6 − benefit) at the call
            // site — feeding the raw benefit would invert it (benefit 0 → "Low Risk" instead of Critical).
            const riskColor = getRiskColorFromScore(6 - scoreData.score);
            const riskLabel = getRiskLabelFromScore(6 - scoreData.score);

            return (
              <div 
                key={pillarId} 
                className="rounded-md border border-border bg-card overflow-hidden transition-colors"
              >
                <button
                  type="button"
                  onClick={() => togglePillar(pillarId)}
                  aria-expanded={isExpanded}
                  aria-controls={`scoring-topic-${pillarId}`}
                  className="w-full flex flex-wrap items-center justify-between gap-3 p-4 hover:bg-state-hover text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-text-muted" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-text-muted" />
                    )}
                    <div>
                      <h4 className="font-semibold text-foreground">{def.label}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-text-secondary font-mono">Weight: {(def.weight * 100).toFixed(0)}%</span>
                        <span className="text-xs text-border-strong">•</span>
                        <span className="text-xs text-text-secondary font-mono">Impact: {(scoreData.score * def.weight).toFixed(2)}</span>
                      </div>
                      {pillarId === 'evidenceQuality' && scoreData.score === 0 ? (
                        <p className="mt-1 text-xs text-text-secondary">Confidence is limited by unresolved evidence checks. Open for the deduction breakdown.</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-medium">{scoreData.score.toFixed(1)}</span>
                    <StatusPill variant={bandStatusPillVariant(riskColor)} className="w-28 justify-center">
                      {pillarId === 'evidenceQuality' ? (siteComposite.confidenceTag === 'V' ? 'High confidence' : siteComposite.confidenceTag === 'R' ? 'Review evidence' : 'Low confidence') : riskLabel}
                    </StatusPill>
                  </div>
                </button>

                {isExpanded && (
                  <div id={`scoring-topic-${pillarId}`} className="p-4 pt-0 border-t border-border bg-card/50">
                    <div className="mt-4 space-y-4">
                      {pillarId === 'evidenceQuality' && <EvidenceQualityDetails composite={siteComposite} />}
                      <div>
                        <h5 className="type-section text-text-muted mb-2">Rationale</h5>
                        <p className="type-body text-foreground leading-relaxed bg-state-hover/50 p-3 rounded border border-border-subtle">
                          {scoreData.rationale || 'No rationale provided.'}
                        </p>
                      </div>

                      {/* L-tier rebuild — the per-fact scoring inputs behind this pillar, each
                          with a provenance chip (SED slot / report stamp / claim / derived /
                          prose heuristic) so the score is traceable to its typed inputs. */}
                      {scoreData.factContributions && scoreData.factContributions.length > 0 && (
                        <div data-testid="pillar-fact-contributions">
                          <h5 className="type-section text-text-muted mb-2">Score inputs</h5>
                          <ul className="space-y-2">
                            {scoreData.factContributions.map((fc: PillarFactContribution, idx: number) => (
                              <li
                                key={`${fc.factKey}-${idx}`}
                                className="flex flex-wrap items-center gap-2"
                                data-testid="pillar-fact-chip"
                              >
                                <StatusPill
                                  variant={PROVENANCE_PILL_VARIANT[fc.factProvenance] ?? 'neutral'}
                                  data-testid={`pillar-fact-provenance-${fc.factProvenance}`}
                                >
                                  {PROVENANCE_LABEL[fc.factProvenance] ?? fc.factProvenance}
                                </StatusPill>
                                <span className="text-xs text-text-secondary font-mono">{fc.factKey}</span>
                                <span className="type-body text-sm text-foreground">{factValueWithUnit(fc)}</span>
                                <span className="text-xs text-text-muted font-mono">
                                  score {fc.score.toFixed(1)} · w {fc.weight}
                                </span>
                                {fc.sourceLabel && (
                                  <span className="text-xs text-text-muted">({fc.sourceLabel})</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* L-tier rebuild — the scoring-critical facts this pillar EXPECTED but
                          could not resolve at any tier. Excluded from the mean (never a silent
                          default score) and listed here as the pillar's evidence gaps. */}
                      {scoreData.missingFacts && scoreData.missingFacts.length > 0 && (
                        <div data-testid="pillar-missing-facts">
                          <h5 className="type-section text-text-muted mb-2">Missing facts</h5>
                          <ul className="space-y-1.5">
                            {scoreData.missingFacts.map((mf: PillarMissingFact) => (
                              <li
                                key={mf.factKey}
                                className="flex items-center gap-2"
                                data-testid="pillar-missing-fact"
                              >
                                <CircleDashed className="w-3.5 h-3.5 text-text-muted shrink-0" />
                                <span className="type-body text-sm text-text-secondary">{mf.label}</span>
                                <span className="text-xs text-text-muted font-mono">{mf.factKey}</span>
                                <span className="text-xs text-text-muted uppercase tracking-wider">excluded from mean</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Part C SP2 — surface the BINDING caps/floors that moved this pillar's
                          score as structured provenance chips (previously buried in the prose
                          rationale). Only rendered when one or more constraints actually fired. */}
                      {scoreData.appliedConstraints && scoreData.appliedConstraints.length > 0 && (
                        <div data-testid="pillar-constraints">
                          <h5 className="type-section text-text-muted mb-2">What moved this score</h5>
                          <ul className="space-y-2">
                            {scoreData.appliedConstraints.map((constraint: PillarConstraint, idx: number) => (
                              <li key={idx} className="flex items-center gap-2" data-testid="pillar-constraint-chip">
                                <Target className="w-3.5 h-3.5 text-text-muted shrink-0" />
                                <StatusPill variant={bandStatusPillVariant(constraintBandColor(constraint.kind))}>
                                  {constraint.label}
                                </StatusPill>
                                {typeof constraint.movedTo === 'number' && (
                                  <span className="text-xs text-text-secondary font-mono">
                                    → risk floored at {constraint.movedTo}
                                  </span>
                                )}
                                {typeof constraint.penalty === 'number' && (
                                  <span className="text-xs text-text-secondary font-mono">
                                    penalty {constraint.penalty.toFixed(2)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {scoreData.citations && scoreData.citations.length > 0 && (
                        <div>
                          <h5 className="type-section text-text-muted mb-2">Citations & Provenance</h5>
                          <ul className="space-y-2">
                            {scoreData.citations.map((citRaw, idx) => {
                              const cit = typeof citRaw === 'string' ? { text: citRaw, docId: undefined, page: undefined } : citRaw;
                              let matchedDoc = cit.docId ? siteDocuments.find(d => d.id === cit.docId) : null;
                              if (!matchedDoc && siteDocuments.length > 0) {
                                matchedDoc = siteDocuments.find(d => cit.text.includes(d.fileName) || (d.summary && cit.text.includes(d.summary))) || null;
                              }

                              return (
                                <li key={idx} className="flex items-start gap-2">
                                  <Target className="w-3.5 h-3.5 text-text-muted mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <span className="type-body text-sm text-text-secondary">{cit.text}</span>
                                    {matchedDoc && (
                                      <button
                                        onClick={() => {
                                          setViewingDoc(matchedDoc);
                                          setViewingPage(cit.page || 1);
                                        }}
                                        className="ml-2 inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover font-medium underline underline-offset-2"
                                      >
                                        <FileText className="w-3 h-3" />
                                        View {cit.page ? `Page ${cit.page}` : 'Document'}
                                      </button>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Source-report links — deliberately OUTSIDE the citations conditional
                          (defect fix): a pillar scored from typed facts may carry no citations
                          at all, but its source reports must still be reachable. */}
                      {scoreData.sourceReportIds && scoreData.sourceReportIds.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="pillar-source-reports">
                          {scoreData.sourceReportIds.map(rid => (
                            <Button
                              key={rid}
                              variant="secondary"
                              size="sm"
                              className="h-7 text-xs gap-1.5"
                              onClick={() => onViewReport(rid)}
                            >
                              <FileText className="w-3 h-3" />
                              View Source Report
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {viewingDoc && (
        <DocViewerDialog
          doc={viewingDoc}
          open={true}
          onClose={() => setViewingDoc(null)}
          initialPage={viewingPage}
        />
      )}
    </div>
  );
};
